/**
 * R227(레드팀 P2-3 ① · P2-10 ②, DESIGN 14.417) — **이름표 카드.** 안전한 땅(마을·전초지·내 길드 영지)에서 사람을 누르면 뜬다.
 *
 * 전에는 사람에게 무엇을 하려면 강공(Ctrl+클릭·A)을 켜야 했고, 같은 몸짓이 마을 안에서는 거래 신청·밖에서는 PK 였다.
 * 모바일은 Ctrl 이 없어 거래하려면 강공을 켜야 했고, 켠 채 사냥터로 나가 파티원을 쳤다. 이 카드가 사람에게 하는 일
 * 셋을 한 자리에 모은다 — [거래]·[파티 초대]·[귓말].
 *
 *   · 판정은 서버가 한다. 여기서 버튼을 끄는 것은 **안 되는 이유를 먼저 말하려는 것**뿐이다(거래 — 서로 마을 안 · 6타일 ·
 *     이미 거래 중이 아닌가 / 파티 — 켜져 있나 · 내가 초대할 수 있나 · 찼나 · 이미 같은 파티인가).
 *   · [차단](묶음 V)은 `/block 이름` 을 채팅으로 보낸다 — 서버가 계정 단위로 저장하고 결과를 기록 줄로 말한다(`src/world/chat.ts`).
 *   · [귓말]은 채팅칸을 `/w 이름 ` 으로 열어 둔다(R230 귓말 · `ui/chat.js` `openChatWith`) — 보내지는 않는다. 채팅이 앞말 채우기를
 *     내보내지 않는 빌드면 버튼을 숨긴다.
 *   · 사람이 시야를 벗어나면 그렇게 말한다(원주민 카드 `resident.js` 와 같은 결). 들고 있던 사본은 쓰지 않는다.
 */
import { S, esc } from '../../core/state.js';
import { where } from '../../world/places.js';
import { PK_WARN } from '../../world/pk.js';
import { fold, isResident, muted, num, sec } from './common.js';
import { levelLabel } from '../../core/level-label.js';
import { starMark } from '../star.js';
import { TRADE_RANGE } from './trade.js';
import * as chat from '../chat.js';

/** 원주민은 원주민 카드(`resident.js`)가 맡는다 — 같은 판정(`isResident`, R257)을 쓴다 */
function selected() { return S.players.find((p) => p.id === S.personId && !isResident(p)) ?? null; }
/** 채팅칸을 앞말(`/w 이름 `)을 채운 채 열 수 있는가 — 없으면 [귓말]을 그리지 않는다 */
export function whisperReady() { return typeof chat.openChatWith === 'function'; }

const klassKo = (k) => (k === 'ranged' ? '원거리' : k === 'assassin' ? '암살자' : '근접');

/** 거래를 걸 수 없는 이유(없으면 null). 서버 `trade.ts` `ask` 의 앞쪽 검사와 같은 뜻이다 */
function tradeBlock(y, p) {
  if (where(y) !== 'town') return '거래는 마을 안에서만 한다(전초지·영지·사냥터에서는 안 된다).';
  if (S.trade) return '이미 다른 거래가 열려 있다 — 거래 창에서 끝낸 뒤 신청한다.';
  if (p.d) return '쓰러져 있는 사람과는 거래할 수 없다.';
  if (where(p) !== 'town') return `${esc(p.n)} 님이 마을 밖에 있다.`;
  const d = Math.hypot(p.x - y.x, p.y - y.y);
  if (d > TRADE_RANGE) return `서로 ${TRADE_RANGE}타일 안에 있어야 한다 — 지금 약 ${Math.ceil(d)}타일.`;
  return null;
}

/** 파티에 초대할 수 없는 이유(없으면 null). 서버 `party.ts` 가 다시 본다 */
function partyBlock(y, p) {
  const party = y.party;
  if (!party) return '파티 정보를 받는 중이다.';
  if (!party.on) return '지금은 파티 기능이 꺼져 있다.';
  const members = party.members || [];
  if (members.some((m) => m.id === p.id)) return '이미 같은 파티다.';
  const leader = party.leader === y.id;
  if (members.length && !leader) return '파티장만 초대할 수 있다.';
  if (members.length >= party.max) return `파티가 찼다(최대 ${num(party.max)}명).`;
  return null;
}

export function stateKey() {
  const p = selected(), y = S.you;
  if (!p || !y) return JSON.stringify([S.personId, null]);
  const d = Math.min(99, Math.ceil(Math.hypot(p.x - y.x, p.y - y.y)));
  const party = y.party;
  return JSON.stringify([p.id, p.n, p.lv, p.k, p.d, p.crime, p.rank, p.title, p.guild?.name ?? '', where(p), where(y), d,
    !!S.trade, party ? [party.on, party.max, party.leader, (party.members || []).map((m) => m.id)] : null, whisperReady()]);
}

export function render() {
  const p = selected(), y = S.you;
  if (!p || !y) return sec('그 사람이 시야를 벗어났다', muted('가까이에서 다시 누르면 이름표 카드가 뜬다.'));
  const name = `${starMark(p.n)}${esc(p.n)}`;
  const sameParty = y.party?.members?.some((m) => m.id === p.id);
  let h = '<section class="ux-hero"><p class="ux-kicker">' + (sameParty ? '함께하는 파티원' : '지금 마주친 사람') + '</p><h3 class="ux-title">' + name + '</h3><p class="ux-summary">'
    + (p.d ? '지금 쓰러져 있다' : `Lv.${levelLabel(p.lv)} · ${klassKo(p.k)}`)
    + (p.guild?.name ? ' · 〈' + esc(p.guild.name) + '〉' : '') + '</p></section>';
  const profile = '<div style="overflow-wrap:anywhere">'
    + `<div class="kv"><span>레벨 · 직업</span><b>Lv.${levelLabel(p.lv)} · ${klassKo(p.k)}</b></div>`
    + (p.guild?.name ? `<div class="kv"><span>길드</span><b>〈${esc(p.guild.name)}〉</b></div>` : '')
    + (p.title ? `<div class="kv"><span>칭호</span><b>${esc(p.title)}</b></div>` : '')
    + (p.crime ? `<div class="kv"><span>범죄 단계</span><b>💀 ${num(p.crime)}</b></div>` : '')
    + (p.rank ? `<div class="kv"><span>전투력 순위</span><b>${num(p.rank)}위</b></div>` : '')
    + (p.d ? muted('지금 쓰러져 있다.') : '')
    + '</div>';

  const trade = tradeBlock(y, p), party = partyBlock(y, p);
  const id = esc(p.id);
  let acts = '<div class="ux-actions row wrap">'
    + `<button ${trade ? 'disabled' : ''} onclick="act.tradeAsk('${id}')">거래 신청</button>`
    + (sameParty ? '<span class="ux-note">같은 파티에서 함께하고 있다</span>' : `<button ${party ? 'disabled' : ''} onclick="act.partyInvite('${id}')">파티 초대</button>`)
    + (whisperReady() ? `<button onclick="act.personWhisper('${id}')">귓말</button>` : '')
    + '</div>';
  if (trade) acts += muted(`거래 — ${trade}`);
  if (party && !sameParty) acts += muted(`파티 — ${party}`);
  // Q276 — 한 줄 요약 + [자세히]. 파티 창 자리는 메뉴 › 사람 › 파티(전에는 「더보기 → 파티」라 적혀 있었다)
  acts += fold('신청·초대는 상대가 수락해야 열린다',
    '거래를 신청하면 상대의 거래 창에 요청이 뜨고, 상대가 수락하면 거래 창이 열린다. '
    + '파티 초대는 상대가 파티 창(메뉴 › 사람 › 파티)에서 수락한다. '
    + '[차단]하면 그 사람(같은 계정의 모든 캐릭터)의 귓말과 채팅(길드 채팅 제외)이 더는 보이지 않는다 — 채팅칸에 「/unblock 이름」으로 푼다.');
  h += sec('이 사람과', acts);
  h += '<details class="ux-details" id="person-profile"><summary>칭호·순위·표식 살펴보기</summary>' + profile + '</details>';
  h += '<details class="ux-details" id="person-block"><summary>대화 차단</summary>' + muted('같은 계정의 모든 캐릭터의 귓말·일반 채팅을 가린다. /unblock 이름으로 해제한다.') + `<button class="ghost" onclick="act.personBlock('${id}')">이 사람 차단하기</button></details>`;
  return h + muted(PK_WARN.how);
}
